// src/components/settings/CompanyProfileSection.js
// Session 78n8, Issue #403. Screen 0 of the #395 document-output gate
// (docs/gates/Stede_DocumentOutput_Gate_395.html), decisions D1 and D1b,
// approved by Colby at Session 78n5.
//
// WHAT THIS IS, in plain words: everything a printed document needs to say
// about who D&R is and where its locations are, kept where leadership can
// change it. Before this screen existed, the whole application held no postal
// address of any kind -- the only field containing the word "address" was a
// printer's IP address -- so a purchase order could not be drawn at all. Not
// "was not". Could not. There was nowhere to put a Ship To, and a vendor
// cannot ship to "Dallas".
//
// It is the FIRST section on the page for the same reason: every other setting
// on that screen adjusts how Stede behaves, and this one says who Stede is
// writing on behalf of.
//
// TWO CONTROLS SHIP VISIBLE AND DISABLED, WITH THEIR REASON SHOWN.
//   * "Add a location" -- D1b. Editing a branch touches one table and one
//     screen; CREATING one touches #252, the four-branch list hardcoded
//     independently across thirteen files, and every one would have to be
//     unwound before a fifth location behaved correctly anywhere. That work is
//     the January 2027 multi-location rollout.
//   * "Replace" on the document logo -- there is no upload path and no storage
//     decision yet. company_settings.logo_url exists and nothing writes it.
// Hiding either one invites somebody to go hunting for it; showing it working
// would be a lie. Showing it disabled with the reason is the honest third
// option, and it is the pattern the gate itself asked for.
//
// Components are declared at module level, never inside a parent's body --
// locked rule 2, a nested component remounts every render and loses input
// focus, which on a form of eleven text fields would be unusable.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { SectionHeading } from './TaxonomyManager';
import { branchShipTo, addressOneLine, hasUsableAddress } from '../../lib/addresses';

// The nine text fields update_company_profile() writes, in the order Screen 0
// draws them. Declared once: the form, the save call and the dirty-check all
// read this list, so a field cannot be added to one and forgotten by another.
// That is the same reasoning the RPC's own audit loop uses.
const PROFILE_FIELDS = [
  { key: 'legal_name', label: 'Legal name', placeholder: 'D&R Signs, Inc.',
    hint: 'The name on contracts and purchase orders. Screens keep showing the shorter name below.' },
  { key: 'street_address', label: 'Street address', placeholder: '4200 Example Blvd' },
  { key: 'address_line2', label: 'Suite, floor or unit', placeholder: 'Optional', optional: true },
  { key: 'city', label: 'City', placeholder: 'Dallas', half: true },
  { key: 'state', label: 'State', placeholder: 'TX', half: true },
  { key: 'postal_code', label: 'ZIP', placeholder: '75201', half: true },
  { key: 'phone', label: 'Main phone', placeholder: '(214) 555-0100', half: true },
  { key: 'website', label: 'Website', placeholder: 'drsigns.net', optional: true },
  { key: 'tax_id', label: 'Tax ID / EIN', placeholder: 'Not set — optional', optional: true,
    hint: 'Standard on a company record and genuinely not required for a purchase order. Overstating a requirement is its own defect.' },
];

const BRANCH_FIELDS = [
  { key: 'street_address', label: 'Street address', placeholder: '4200 Example Blvd' },
  { key: 'address_line2', label: 'Suite, floor or unit', placeholder: 'Optional', optional: true },
  { key: 'city', label: 'City', placeholder: 'Dallas', half: true },
  { key: 'state', label: 'State', placeholder: 'TX', half: true },
  { key: 'postal_code', label: 'ZIP', placeholder: '75201', half: true },
  { key: 'phone', label: 'Phone', placeholder: '(214) 555-0100', half: true },
  { key: 'receiving_contact_name', label: 'Receiving contact', placeholder: 'Who the carrier asks for',
    optional: true,
    hint: 'A name, not a Stede account — the person on the dock often does not have one.' },
];

// An empty text box and a cleared text box must both store NULL, never ''.
// An empty string is a value: it prints as a blank line on a document sent to
// a vendor, and it makes "has an address" impossible to ask in SQL.
function nullIfBlank(v) {
  const t = (v || '').trim();
  return t === '' ? null : t;
}

export function CompanyProfileSection({ settings, onSaved }) {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <SectionHeading>Company profile</SectionHeading>
      <p style={{ fontSize: 13, color: '#5C5C5C', margin: '10px 0 4px', lineHeight: 1.5 }}>
        These appear on every purchase order, register and exported PDF. Changing
        them here changes every document produced from that point on — it does not
        rewrite documents already sent.
      </p>
      <CompanyIdentityForm settings={settings} onSaved={onSaved} />
      <LocationsList settings={settings} />
    </div>
  );
}

function CompanyIdentityForm({ settings, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {};
    PROFILE_FIELDS.forEach(f => { init[f.key] = settings[f.key] || ''; });
    return init;
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedNote, setSavedNote] = useState('');

  const dirty = PROFILE_FIELDS.some(
    f => nullIfBlank(form[f.key]) !== (settings[f.key] ?? null)
  );

  async function handleSave() {
    setSaving(true);
    setError('');
    setSavedNote('');
    const { error: rpcError } = await supabase.rpc('update_company_profile', {
      p_legal_name: nullIfBlank(form.legal_name),
      p_street_address: nullIfBlank(form.street_address),
      p_address_line2: nullIfBlank(form.address_line2),
      p_city: nullIfBlank(form.city),
      p_state: nullIfBlank(form.state),
      p_postal_code: nullIfBlank(form.postal_code),
      p_phone: nullIfBlank(form.phone),
      p_website: nullIfBlank(form.website),
      p_tax_id: nullIfBlank(form.tax_id),
      p_reason: nullIfBlank(reason),
    });
    setSaving(false);
    if (rpcError) { setError('Could not save that. ' + rpcError.message); return; }
    setReason('');
    setSavedNote('Saved. Every document from now on carries these details.');
    await onSaved();
  }

  return (
    <>
      {/* The short name is shown and NOT editable here. It is company_name,
          which every screen in the app renders, and it is not part of this
          RPC -- changing what the product calls itself is a different decision
          from correcting the postal address, and folding the two together
          would put a rename behind a Save button labelled for an address. */}
      <div style={{ padding: '14px 0', borderBottom: '0.5px solid #E0E0E0' }}>
        <p style={{ fontSize: 12, fontWeight: 500, color: '#5C5C5C', margin: '0 0 4px' }}>Displayed as</p>
        <p style={{ fontSize: 14, color: '#0F0F0F', margin: 0 }}>
          {settings.company_name}
          <span style={{ fontSize: 12, color: '#9A9A9A', marginLeft: 8 }}>
            the short name every screen shows
          </span>
        </p>
      </div>

      <FieldGrid fields={PROFILE_FIELDS} values={form} onChange={(k, v) => setForm(s => ({ ...s, [k]: v }))} />

      {/* No upload path exists. Shown disabled with the reason rather than
          hidden -- same call as "Add a location" below. */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, padding: '14px 0', borderBottom: '0.5px solid #E0E0E0',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>Logo on documents</p>
          <p style={{ fontSize: 12, color: '#7A7A7A', margin: 0, lineHeight: 1.5 }}>
            Documents print the company name as text for now. Uploading a logo
            arrives with the document build.
          </p>
        </div>
        <button disabled title="No upload path exists yet"
          style={{
            padding: '8px 14px', background: '#F5F5F3', color: '#9A9A9A',
            border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: 'not-allowed', flexShrink: 0,
          }}>
          Replace
        </button>
      </div>

      <div style={{ padding: '14px 0' }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 8px' }}>Reason for this change (optional)</p>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. corrected the suite number after the move"
          style={{
            width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8,
            padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 12,
          }} />
        <p style={{ fontSize: 11.5, color: '#7A7A7A', margin: '0 0 12px', lineHeight: 1.5 }}>
          Every change to these fields is recorded with who made it and when,
          because they print on documents that leave the company.
        </p>
        {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        {savedNote && <div style={{ background: '#E8F5EF', color: '#146B50', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{savedNote}</div>}
        <button onClick={handleSave} disabled={saving || !dirty}
          style={{
            padding: '10px 20px',
            background: (saving || !dirty) ? '#E0E0E0' : '#1D9E75',
            color: (saving || !dirty) ? '#9A9A9A' : 'white',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
          }}>
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>
    </>
  );
}

// Shared field renderer. `half` fields pair up on desktop and stack on a phone,
// which is what makes City/State/ZIP/Phone read as an address block rather than
// four unrelated boxes. flexWrap does the responsive work with no media query
// and no width listener.
//
// Every input is 16px. Anything smaller triggers iOS auto-zoom on focus --
// locked rule 12, and this is a form a manager will fill in on a phone.
function FieldGrid({ fields, values, onChange }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 14,
      padding: '14px 0', borderBottom: '0.5px solid #E0E0E0',
    }}>
      {fields.map(f => (
        <div key={f.key} style={{ flex: f.half ? '1 1 120px' : '1 1 100%', minWidth: f.half ? 120 : '100%' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#5C5C5C', margin: '0 0 6px' }}>
            {f.label}
            {f.optional && <span style={{ color: '#9A9A9A', fontWeight: 400 }}> · optional</span>}
          </label>
          <input
            type="text"
            value={values[f.key] || ''}
            placeholder={f.placeholder}
            onChange={e => onChange(f.key, e.target.value)}
            style={{
              width: '100%', height: 38, border: '1px solid #E0E0E0', borderRadius: 8,
              padding: '0 10px', fontSize: 16, boxSizing: 'border-box', background: 'white',
            }}
          />
          {f.hint && (
            <p style={{ fontSize: 11.5, color: '#7A7A7A', margin: '6px 0 0', lineHeight: 1.5 }}>{f.hint}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function LocationsList({ settings }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  // A failed load must not render as "no locations". An empty list that is
  // really a broken query is a recurring failure mode in this codebase, and
  // here it would read as "this company has no branches".
  const load = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from('branches')
      .select('id, name, city, street_address, address_line2, state, postal_code, phone, receiving_contact_name, uses_company_address, is_transfer_hub')
      .order('id');
    if (qErr) { setError('Could not load locations: ' + qErr.message); return; }
    setError('');
    setRows(data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, paddingBottom: 8, borderBottom: '0.5px solid #E0E0E0', marginBottom: 4,
      }}>
        <p style={{
          fontSize: 11, fontWeight: 500, color: '#9A9A9A', textTransform: 'uppercase',
          letterSpacing: '0.06em', margin: 0,
        }}>
          Locations{rows ? ' · ' + rows.length : ''}
        </p>
      </div>

      <p style={{ fontSize: 13, color: '#5C5C5C', margin: '10px 0 4px', lineHeight: 1.5 }}>
        Each location's address is the Ship To on any order raised there. An
        order cannot be sent from a location with no address.
      </p>

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, margin: '10px 0' }}>{error}</div>}
      {!rows && !error && <div style={{ fontSize: 12, color: '#9A9A9A', padding: '14px 0' }}>Loading…</div>}

      {rows && rows.map(b => (
        <LocationRow key={b.id} branch={b} settings={settings} onEdit={() => setEditing(b)} />
      ))}

      {/* D1b. Visible, disabled, reason shown. */}
      <div style={{ padding: '14px 0' }}>
        <button disabled
          style={{
            padding: '10px 20px', background: '#F5F5F3', color: '#9A9A9A',
            border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: 'not-allowed',
          }}>
          Add a location
        </button>
        <p style={{ fontSize: 11.5, color: '#7A7A7A', margin: '8px 0 0', lineHeight: 1.5 }}>
          Adding and removing locations arrives with the multi-location rollout.
          The four existing ones can be edited freely.
        </p>
      </div>

      {editing && (
        <LocationEditor
          branch={editing}
          settings={settings}
          otherNames={(rows || []).filter(r => r.id !== editing.id).map(r => r.name)}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

// ── THE HUB SWITCH'S WORDS -- #691 half 1, gate 665 D10, Session 79.22b.4.4 ──
//
// The switch has set the DEFAULT ROUTE for every load since 79.10: switched
// on, a load between two other locations starts out set to drop here. Its
// words said only that loads "can be dropped here", so nobody reading them
// could tell that switching it off sends every load straight through. Colby
// asked at 79.22b.4.1's open for a setting deciding hub-or-direct; this is
// that setting, and it now says so. Gate 665 F2, word for word.
//
// The example uses the company's OWN location names (F2), taken from the
// other locations in list order. Per-route exceptions and a second hub are
// #691 half 2 (Oct 2-3), not this.
export const HUB_SWITCH_LABEL = 'Loads between two other locations stop here by default';

export function hubSwitchHelper(otherNames = []) {
  const [a, b, c] = (otherNames || []).filter(Boolean);
  const example = a && b && c
    ? `A load from ${a} to ${b}, or from ${b} to ${c}, will start`
    : a && b
      ? `A load from ${a} to ${b} will start`
      : 'A load between two other locations will start';
  // #697, Session 79.22b.5.2 -- what this location SENDS is collected here too
  // (gate 665 D23, R2), so "Loads that start or end here are not affected" was
  // no longer true. The new sentences are the ones Colby approved at its open
  // (R1, docs/audits/79.22b.5.2/APPROVED_DRAWING_697_644.html, drawing 3).
  return `${example} out set to drop here for the receiving branch to collect. `
    + 'Whoever loads can still send one straight through. '
    + 'What this location sends waits here for the receiving branch to collect, too. '
    + 'Loads coming to this location are not affected. '
    + 'Switch this off and every load goes straight to where it is going, this location\'s own included.';
}

function LocationRow({ branch, settings, onEdit }) {
  // Resolved through the shared lib, not read off the branch row, so this list
  // shows exactly what a purchase order would print. A row that displayed the
  // branch's own stored columns while the document printed the inherited ones
  // would be a screen quietly disagreeing with the paperwork.
  const shipTo = branchShipTo(branch, settings);
  const set = hasUsableAddress(shipTo);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 16, padding: '14px 0', borderBottom: '0.5px solid #F0F0F0',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 500, color: '#0F0F0F', margin: '0 0 2px' }}>{branch.name}</p>
        {/* Callout A: an unset address states the CONSEQUENCE, not that a
            field is empty. "Needed before an order can ship here" tells a
            leader why they should care; "required" does not.
            An INHERITED but still-empty address gets its own sentence, because
            "not set" would send someone to edit this row when the thing to fix
            is the company address above. */}
        <p style={{ fontSize: 12, color: set ? '#5C5C5C' : '#B0791F', margin: 0, lineHeight: 1.5 }}>
          {set
            ? addressOneLine(shipTo)
            : branch.uses_company_address
              ? 'Follows the company address, which is not filled in yet'
              : 'Address not set — needed before an order can ship here'}
        </p>
        {set && branch.uses_company_address && (
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 0' }}>
            Same as the company address
          </p>
        )}
        {/* #553 D15. The switch itself lives in the editor, but WHICH location
            is the hub has to be readable from the list -- otherwise the only
            way to find out is to open all four in turn, and the one fact this
            list exists to show is what each location is for. */}
        {branch.is_transfer_hub && (
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 0' }}>
            {/* #691 half 1 -- the switch's own words, so the list and the
                editor say the same thing. */}
            {HUB_SWITCH_LABEL}
          </p>
        )}
      </div>
      <button onClick={onEdit}
        style={{
          padding: '8px 14px', background: 'white', color: '#0F0F0F',
          border: '1px solid #D8D8D8', borderRadius: 8, fontSize: 13,
          fontWeight: 700, cursor: 'pointer', flexShrink: 0,
        }}>
        {set ? 'Edit' : 'Add address'}
      </button>
    </div>
  );
}

// Inline expanding editor rather than an overlay. One branch is edited at a
// time from a list of four, and a modal would be a heavier interaction than
// the task deserves -- it also keeps this component clear of the position:fixed
// rule entirely (locked rule 3), since there is no fixed positioning to get
// wrong.
function LocationEditor({ branch, settings, otherNames = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {};
    BRANCH_FIELDS.forEach(f => { init[f.key] = branch[f.key] || ''; });
    return init;
  });
  const [inherit, setInherit] = useState(!!branch.uses_company_address);
  // #553 D15. The hub flag has existed on `branches` since Sep 11 and NOTHING
  // IN THE APPLICATION COULD CHANGE IT -- Austin's was set by a database
  // script. The flag was already the right shape; it had no door, which is
  // #515's defect one screen over. It belongs here rather than on a new screen
  // because a hub IS a location, and the gate's D14 settled that a drop-off is
  // always somewhere already registered -- Colby, asked directly whether a
  // load is ever left at a rented yard or a customer site: "no."
  const [isHub, setIsHub] = useState(!!branch.is_transfer_hub);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const preview = branchShipTo(
    { ...branch, ...form, uses_company_address: inherit },
    settings
  );

  async function handleSave() {
    setSaving(true);
    setError('');
    const patch = { uses_company_address: inherit, is_transfer_hub: isHub };
    // The branch's own address columns are written even while inheriting, so
    // whatever was typed survives un-ticking the box. Ticking is reversible;
    // blanking the columns here would make it a one-way door.
    BRANCH_FIELDS.forEach(f => { patch[f.key] = nullIfBlank(form[f.key]); });
    // Single table, so a plain update rather than an RPC -- locked rule 7
    // governs writes that must span tables together, and this is one row.
    const { error: updErr } = await supabase
      .from('branches')
      .update(patch)
      .eq('id', branch.id);
    setSaving(false);
    if (updErr) { setError('Could not save that. ' + updErr.message); return; }
    await onSaved();
  }

  return (
    <div style={{
      border: '1px solid #E0E0E0', borderRadius: 10, padding: '4px 14px 14px',
      background: '#FAFAF8', marginTop: 12,
    }}>
      <p style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', margin: '14px 0 0' }}>
        {branch.name}
      </p>
      <p style={{ fontSize: 12, color: '#7A7A7A', margin: '2px 0 0', lineHeight: 1.5 }}>
        This is what a vendor reads as the Ship To on an order raised here.
      </p>

      {/* Never ticked by default, on any location -- Colby's instruction at
          78n8. Two addresses looking alike is not evidence they are the same
          building, and guessing wrong puts a wrong address on a document
          nobody re-reads. Somebody ticks this on purpose. */}
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        padding: '14px 0 0',
      }}>
        <input
          type="checkbox"
          checked={inherit}
          onChange={e => setInherit(e.target.checked)}
          style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, cursor: 'pointer' }}
        />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#0F0F0F' }}>
            Same as the company address
          </span>
          <span style={{ display: 'block', fontSize: 12, color: '#7A7A7A', marginTop: 2, lineHeight: 1.5 }}>
            For a location in the same building as the head office. It follows the
            company address rather than copying it, so changing that address once
            keeps this location's paperwork right too.
          </span>
        </span>
      </label>

      {/* #553 D15 — THE HUB SWITCH, and it sits here rather than on a screen of
          its own because a hub IS a location. One more field beside the name,
          the address and the receiving contact.

          WHAT IT CHANGES for a person: Houston's load sheet gains "Leave at
          {this location}" as somewhere a truck can drop material for another
          branch to collect later. Austin is the one D&R uses — Houston runs
          material up, Dallas fetches it when it suits them.

          Nothing about it moves stock or grants anybody an action, so it needs
          no permission of its own beyond the Company Settings door it is
          already behind. */}
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        padding: '14px 0 0',
      }}>
        <input
          type="checkbox"
          checked={isHub}
          onChange={e => setIsHub(e.target.checked)}
          style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, cursor: 'pointer' }}
        />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#0F0F0F' }}>
            {HUB_SWITCH_LABEL}
          </span>
          {/* #691 half 1. SUPERSEDED helper, kept here because its facts are
              still true: "A staging point on the way to somewhere else.
              Material left here is off the sending branch's shelf and not yet
              on the receiving one, and it stays on the collecting branch's list
              until somebody fetches it." It never said the switch sets the
              default route, which is what it does. */}
          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.5 }}>
            {hubSwitchHelper(otherNames)}
          </span>
        </span>
      </label>

      {inherit && (
        <div style={{
          background: '#F0F6F4', border: '1px solid #D6E7E0', borderRadius: 8,
          padding: '10px 12px', margin: '12px 0 0',
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#146B50', margin: '0 0 4px', letterSpacing: '0.04em' }}>
            SHIP TO, AS A VENDOR WILL SEE IT
          </p>
          {hasUsableAddress(preview) ? (
            <p style={{ fontSize: 12.5, color: '#0F0F0F', margin: 0, lineHeight: 1.6 }}>
              {addressOneLine(preview)}
            </p>
          ) : (
            <p style={{ fontSize: 12.5, color: '#B0791F', margin: 0, lineHeight: 1.6 }}>
              The company address above is not filled in yet, so this location
              would still have nothing to ship to. Fill that in first.
            </p>
          )}
        </div>
      )}

      {/* The fields stay visible and editable while inheriting rather than being
          hidden. Hiding them would erase the evidence of what un-ticking the box
          restores, and a person needs to see that this location's own address is
          still on file before they trust the checkbox. */}
      <div style={{ opacity: inherit ? 0.45 : 1 }}>
        <FieldGrid fields={BRANCH_FIELDS} values={form} onChange={(k, v) => setForm(s => ({ ...s, [k]: v }))} />
      </div>
      {inherit && (
        <p style={{ fontSize: 11.5, color: '#7A7A7A', margin: '8px 0 0', lineHeight: 1.5 }}>
          These are this location's own details, kept on file. Documents use the
          company address while the box above is ticked; un-tick it and these come
          back. The phone is the exception — a location's own number is used for
          the carrier even while the address is inherited.
        </p>
      )}

      {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '8px 12px', fontSize: 12, margin: '12px 0' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, paddingTop: 14 }}>
        <button onClick={handleSave} disabled={saving}
          style={{
            padding: '10px 20px', background: saving ? '#E0E0E0' : '#1D9E75',
            color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          }}>
          {saving ? 'Saving...' : 'Save location'}
        </button>
        <button onClick={onClose} disabled={saving}
          style={{
            padding: '10px 20px', background: 'white', color: '#5C5C5C',
            border: '1px solid #D8D8D8', borderRadius: 8, fontSize: 13,
            fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
          }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
